import { useCallback, useMemo, useSyncExternalStore } from 'react';

/**
 * Is this screen wide enough for the duel's desktop composition?
 *
 * `layout.css` puts every other decision about width in CSS, where it belongs,
 * and this exists for the one thing CSS cannot reach: a `viewBox`. The two
 * comparison plots and the race's live chart declare their drawing box as an
 * HTML attribute, and an attribute is not a stylesheet — no media query can swap
 * 320x200 for 640x240 at a breakpoint. So the breakpoint has to be READ, once,
 * by the components that draw a frame.
 *
 * It is deliberately the SAME number as the sheets use (`--stage-max` and the
 * chassis both step at 1024), and that duplication is the reason this file has a
 * comment rather than being a one-liner: the query below and the `@media` rules
 * in `compare.css`, `raceStage.css` and `dragRace.css` are one decision spelled
 * in two languages, and a change to either is a change to both.
 *
 * SUBSCRIBED rather than read once, which is where it parts company with
 * `prefersReducedMotion` in `useDragRace.js`. That one is read at call time on
 * the argument that a reader who changes a system setting mid-race will get the
 * new answer next race — true, because nobody changes it mid-race. A window
 * resize is not that: dragging a browser window across 1024px is an ordinary
 * thing to do, and a chart that kept a 320-unit frame in a 1100px column until
 * the next navigation would be the exact poster this frame exists to prevent.
 *
 * `useSyncExternalStore` rather than a `useState` plus an effect, because that
 * pair renders once with the wrong answer and then corrects itself — one frame
 * of a phone-sized chart on every desktop mount.
 */

/** The chassis's first step, quoted from `layout.css`. */
export const WIDE_QUERY = '(min-width: 1024px)';

/**
 * `matchMedia` is a defaulted PARAMETER, the same shape `CarPrizeReveal.js` uses.
 *
 * jsdom in this repo has no `window.matchMedia` at all, so a component that
 * reached for it unguarded would take every chart test down; a browser without
 * it answers false and gets the phone frame, which is the honest degradation.
 */
export default function useWideStage(
    matchMedia = typeof window === 'undefined' ? null : window.matchMedia,
) {
    const query = useMemo(
        () => matchMedia?.call(window, WIDE_QUERY) ?? null,
        [matchMedia],
    );

    const subscribe = useCallback((notify) => {
        query?.addEventListener('change', notify);

        return () => query?.removeEventListener('change', notify);
    }, [query]);

    return useSyncExternalStore(
        subscribe,
        () => Boolean(query?.matches),
        // No server render in this product, but the third argument is what stops
        // React warning about one — and false is what a page with no window is.
        () => false,
    );
}
